import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import { visitApi } from 'src/api/visit';
import { GetVisitById } from 'woofwoof-api';
import { useOrganizationContext } from '../../organization/hooks/useOrganizationContext';

// ----------------------------------------------------------------------

// Which editor the Notatki tab shows. Kept here rather than inside the tab so
// the assistant — which lives in the side panel, outside that route — can open
// the tab on the side it just filled.
export type VisitNotesMode = 'patient' | 'internal';

// Which side-panel tab is showing. Lives here because starting a transcription
// happens in the header actions, outside the panel, and should bring the
// transcript into view.
export type VisitSidePanelTab = 'assistant' | 'transcription';

export type CurrentVisitContextType = {
  data?: GetVisitById;
  pendingVisits: GetVisitById[];
  getVisitByProfileId: (profileId: string) => GetVisitById | undefined;
  getVisitById: (visitId: string) => GetVisitById | undefined;
  refetch: () => Promise<any>;
  isLoading: boolean;
  isError: boolean;
  notesMode: VisitNotesMode;
  setNotesMode: Dispatch<SetStateAction<VisitNotesMode>>;
  sidePanelTab: VisitSidePanelTab;
  setSidePanelTab: Dispatch<SetStateAction<VisitSidePanelTab>>;
  // Lets components outside the visit header (e.g. the Transcript tab) trigger
  // the transcription start flow that lives in VisitTabsActions.
  transcriptionStartHandlerRef: MutableRefObject<(() => void) | null>;
};

export const CurrentVisitContext =
  createContext<CurrentVisitContextType | null>(null);

type CurrentVisitProviderProps = {
  children: React.ReactNode;
};

export function CurrentVisitProvider({ children }: CurrentVisitProviderProps) {
  const { isProfessionalRoleSelected, selectedRole, selectedOrganization } =
    useOrganizationContext();
  const [notesMode, setNotesMode] = useState<VisitNotesMode>('patient');
  const [sidePanelTab, setSidePanelTab] =
    useState<VisitSidePanelTab>('assistant');
  const transcriptionStartHandlerRef = useRef<(() => void) | null>(null);

  const { refetch, data, isLoading, isError } = useQuery(
    ['current-visit-professional', selectedRole, selectedOrganization?.id],
    () => visitApi.visitControllerGetPendingProfessionalVisits(),
    {
      retry: 3,
      enabled: isProfessionalRoleSelected,
      refetchInterval: 10000,
      cacheTime: 0,
    },
  );

  const pendingVisits = useMemo(() => data?.data ?? [], [data]);

  const getVisitByProfileId = useCallback(
    (profileId: string): GetVisitById | undefined =>
      pendingVisits.find((visit) => visit.profile?.id === profileId),
    [pendingVisits],
  );

  const getVisitById = useCallback(
    (visitId: string): GetVisitById | undefined =>
      pendingVisits.find((visit) => visit.id === visitId),
    [pendingVisits],
  );

  const memoizedValue = useMemo(() => {
    return {
      data: pendingVisits[0],
      pendingVisits,
      getVisitByProfileId,
      getVisitById,
      isLoading,
      isError,
      refetch,
      notesMode,
      setNotesMode,
      sidePanelTab,
      setSidePanelTab,
      transcriptionStartHandlerRef,
    };
  }, [
    pendingVisits,
    getVisitByProfileId,
    getVisitById,
    isLoading,
    isError,
    refetch,
    notesMode,
    setNotesMode,
    sidePanelTab,
    setSidePanelTab,
    transcriptionStartHandlerRef,
  ]);

  return (
    <CurrentVisitContext.Provider value={memoizedValue}>
      {children}
    </CurrentVisitContext.Provider>
  );
}
